import { Box, Typography } from '@mui/material';
import React from 'react';

import CustomDialog from 'src/components/custom-dialog/custom-dialog';
import CustomTableCell from 'src/components/custom-table-cell/custom-table-cell';
import DataTable from 'src/components/data-table/data-table';
import Page from 'src/components/page/page';

import BenefitFilter from '../benefit-filter/benefit-filter';
import BenefitForm from '../benefit-form/benefit-form';
import { useBenefits } from './benefits.hook';

function RowConfig({ row, categories, stores, levels, restaurants }: any) {
  const formatEndDate = (endDate?: string | Date | { $date: string }): string => {
    try {
      if (!endDate) return '-';
      let date;
      if (typeof endDate === 'object' && endDate !== null && '$date' in endDate) {
        date = new Date(endDate.$date);
      } else {
        date = new Date(endDate);
      }

      if (Number.isNaN(date.getTime())) {
        return '-';
      }

      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      console.error('Error formatting date:', error, endDate);
      return '-';
    }
  };

  const formatCategory = (categoryId?: any): string => {
    try {
      if (!categoryId) return '-';

      if (Array.isArray(categoryId)) {
        if (categoryId.length === 0) return '-';
        categoryId = categoryId[0];
      }

      if (typeof categoryId === 'object' && categoryId !== null && categoryId.$oid) {
        categoryId = categoryId.$oid;
      }

      const category = categories?.find((cat: any) => cat.id === categoryId || cat._id === categoryId || cat.value === categoryId);

      return category?.name || category?.label || 'Categoria';
    } catch (error) {
      console.error('Error formatting category:', error, categoryId);
      return '-';
    }
  };

  const formatStore = (storeId?: any): string => {
    try {
      if (!storeId) return '-';
      if (typeof storeId === 'object' && storeId !== null && storeId.$oid) {
        storeId = storeId.$oid;
      }

      const store = stores?.find((st: any) => st.id === storeId || st._id === storeId || st.value === storeId);

      return store?.name || store?.label || '-';
    } catch (error) {
      console.error('Error formatting store:', error, storeId);
      return '-';
    }
  };

  const formatRestaurant = (restaurantId?: any): string => {
    try {
      if (!restaurantId) return '-';
      if (typeof restaurantId === 'object' && restaurantId !== null && restaurantId.$oid) {
        restaurantId = restaurantId.$oid;
      }

      const restaurant = restaurants?.find((st: any) => st.id === restaurantId || st._id === restaurantId || st.value === restaurantId);

      return restaurant?.name || restaurant?.label || '-';
    } catch (error) {
      console.error('Error formatting restaurant:', error, restaurantId);
      return '-';
    }
  };

  const formatEvent = (event?: any): string => {
    try {
      if (!event) return '-';
      if (typeof event === 'object' && event !== null) {
        if (event.$oid) {
          return 'Evento';
        }
        return event.name || event.label || 'Evento';
      }
      return String(event) || '-';
    } catch (error) {
      console.error('Error formatting event:', error, event);
      return '-';
    }
  };

  const formatLevels = (levelIds?: any[] | any): string => {
    try {
      if (!levelIds) return '-';

      if (Array.isArray(levelIds)) {
        if (levelIds.length === 0) return '-';

        const levelNames = levelIds
          .map((levelId) => {
            if (typeof levelId === 'object' && levelId !== null && levelId.$oid) {
              levelId = levelId.$oid;
            }

            const level = levels?.find((lv: any) => lv.id === levelId || lv._id === levelId || lv.value === levelId);

            return level?.name || level?.label || level?.level;
          })
          .filter(Boolean);

        return levelNames.length > 0 ? levelNames.join(', ') : '-';
      }

      let levelId = levelIds;
      if (typeof levelIds === 'object' && levelIds !== null && levelIds.$oid) {
        levelId = levelIds.$oid;
      }

      const level = levels?.find((lv: any) => lv.id === levelId || lv._id === levelId || lv.value === levelId);

      return level?.name || level?.label || level?.level || '-';
    } catch (error) {
      console.error('Error formatting levels:', error, levelIds);
      return '-';
    }
  };

  const translateType = (type?: string) => {
    if (!type) return '-';
    const translations: { [key: string]: string } = {
      voucher: 'Voucher',
      informative: 'Informativo',
      discount: 'Desconto',
      cashback: 'Cashback',
      parking: 'Estacionamento',
    };
    return translations[type] || type;
  };

  return (
    <>
      <CustomTableCell borderLeft>
        <Typography typography="body-md">{row?.name || '-'}</Typography>
      </CustomTableCell>
      <CustomTableCell>
        <Typography typography="body-sm">{translateType(row?.type)}</Typography>
      </CustomTableCell>
      <CustomTableCell>
        <Typography typography="body-md">{formatCategory(row?.category)}</Typography>
      </CustomTableCell>
      <CustomTableCell>
        <Typography typography="body-md">{formatStore(row?.store)}</Typography>
      </CustomTableCell>
      <CustomTableCell>
        <Typography typography="body-md">{formatRestaurant(row?.restaurant)}</Typography>
      </CustomTableCell>
      <CustomTableCell>
        <Typography typography="body-md">{formatEvent(row?.event)}</Typography>
      </CustomTableCell>
      <CustomTableCell>
        <Typography typography="body-md">{formatLevels(row?.levels)}</Typography>
      </CustomTableCell>
      <CustomTableCell>
        <Typography typography="body-md">{formatEndDate(row?.endDate)}</Typography>
      </CustomTableCell>
    </>
  );
}

export interface BenefitsProps {}

export default function Benefits(props: BenefitsProps) {
  const {
    tableHeader,
    benefits,
    isLoading,
    order,
    orderBy,
    checked,
    page,
    total,
    limit,
    useConfirmDeleteDialog,
    handleDelete,
    handleConfirmDelete,
    handleChangePage,
    handleAddNew,
    handleChangeRowsPerPage,
    handleChangeStatus,
    createSortHandler,
    getData,
    categories,
    stores,
    restaurants,
    levels,
  } = useBenefits();

  return (
    <Page title="Benefícios">
      <BenefitFilter handleAddNew={handleAddNew} getData={getData} />
      <Box height="32px" />
      <DataTable
        collapseNewIdCondition="-"
        rows={benefits}
        header={tableHeader}
        collapseConfig={(row) => <BenefitForm getData={getData} row={row} />}
        rowConfig={(row) => (
          <RowConfig row={row} categories={categories} stores={stores} levels={levels} restaurants={restaurants} eventsOptions={[]} />
        )}
        createSortHandler={createSortHandler}
        handleDelete={handleDelete}
        order={order}
        orderBy={orderBy}
        checked={checked}
        count={total}
        isLoading={isLoading}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        page={page}
        rowsPerPage={limit}
        setChecked={handleChangeStatus}
      />
      {useConfirmDeleteDialog.value && (
        <CustomDialog
          open={useConfirmDeleteDialog.value}
          icon="alert-line"
          title="Atenção"
          content="Deseja mesmo deletar o benefício?"
          confirmText="Remover"
          cancelText="Cancelar"
          onConfirm={handleConfirmDelete}
          onClose={useConfirmDeleteDialog.onFalse}
        />
      )}
    </Page>
  );
}
